import type { AdminGateway } from '../application/adminGateway';
import type { AccessResult } from '../domain/access';
import type {
  AdminAccount,
  AdminAccountsResult,
  AdminAgency,
  AdminAgenciesResult,
  AdminAuditEvent,
  AdminAuditResult,
  AdminCreateInvitationResult,
  AdminInvitation,
  AdminInvitationsResult,
  AdminMutationResult,
  AdminOverview,
  AdminOverviewResult,
  AdminEntitlement,
  AdminEntitlementsResult,
  AdminDependent,
  AdminDependentsResult,
  CommercialPlan,
  AdminPlansResult,
} from '../domain/admin';
import type { SupabaseAccessGateway } from './supabaseAccessGateway';

const sessionKey = 'alpha-mls:supabase-session:v1';
const env = import.meta.env as Record<string, string | undefined>;

interface StoredSession {
  readonly accessToken: string;
  readonly expiresAt: number;
}

interface OverviewRow {
  readonly profiles: number | string;
  readonly pending_invitations: number | string;
  readonly redeemed_invitations: number | string;
  readonly superusers: number | string;
  readonly audit_events: number | string;
}

interface AgencyRow {
  readonly agency_id: string;
  readonly name: string;
  readonly status: string;
}

interface InvitationRow {
  readonly invitation_id: string;
  readonly email: string;
  readonly invited_role: string;
  readonly status: string;
  readonly agency_id: string | null;
  readonly agency_name: string | null;
  readonly invited_by: string;
  readonly invited_by_name: string | null;
  readonly redeemed_by: string | null;
  readonly redeemed_by_name: string | null;
  readonly expires_at: string;
  readonly created_at: string;
}

interface AccountRow {
  readonly profile_id: string;
  readonly display_name: string;
  readonly email: string | null;
  readonly profile_status: string;
  readonly public_name: string | null;
  readonly agency_name: string | null;
  readonly agency_role: string | null;
  readonly global_roles: string[];
  readonly created_at: string;
}

interface PlanRow {
  readonly plan_key: string;
  readonly display_name: string;
  readonly dependent_limit: number | string;
  readonly property_limit: number | string;
  readonly photos_per_property: number | string;
  readonly payment_mode: string;
}

interface EntitlementRow {
  readonly holder_profile_id: string;
  readonly holder_name: string;
  readonly plan_key: string;
  readonly plan_name: string;
  readonly entitlement_status: string;
  readonly dependent_limit: number | string;
  readonly dependents_used: number | string;
  readonly property_limit: number | string;
  readonly properties_used: number | string;
  readonly photos_per_property: number | string;
  readonly payment_mode: string;
}

interface DependentRow {
  readonly dependent_profile_id: string;
  readonly display_name: string;
  readonly status: string;
}

interface AuditRow {
  readonly event_id: number | string;
  readonly actor_profile_id: string | null;
  readonly actor_name: string | null;
  readonly action: string;
  readonly entity_type: string;
  readonly entity_id: string | null;
  readonly metadata: Record<string, unknown>;
  readonly occurred_at: string;
}

function readAccessToken(): string | null {
  try {
    const raw = window.localStorage.getItem(sessionKey);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as Partial<StoredSession>;
    if (
      typeof parsed.accessToken !== 'string' ||
      typeof parsed.expiresAt !== 'number' ||
      parsed.expiresAt <= Math.floor(Date.now() / 1000)
    ) {
      return null;
    }
    return parsed.accessToken;
  } catch {
    return null;
  }
}

function toHex(bytes: Uint8Array): string {
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

function base64Url(bytes: Uint8Array): string {
  const raw = String.fromCharCode(...bytes);
  return btoa(raw).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/u, '');
}

async function invitationSecret(): Promise<{ token: string; digest: string }> {
  const bytes = new Uint8Array(32);
  window.crypto.getRandomValues(bytes);
  const token = base64Url(bytes);
  const digestBuffer = await window.crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(token),
  );
  return { token, digest: toHex(new Uint8Array(digestBuffer)) };
}

export class SupabaseAdminGateway implements AdminGateway {
  constructor(private readonly accessGateway: SupabaseAccessGateway) {}

  resolveCurrentIdentity(signal?: AbortSignal): Promise<AccessResult> {
    return this.accessGateway.resolveCurrentIdentity(signal);
  }

  signOut(signal?: AbortSignal): Promise<void> {
    return this.accessGateway.signOut(signal);
  }

  private async rpc<T>(
    name: string,
    body: Record<string, unknown>,
    signal?: AbortSignal,
  ): Promise<T | null> {
    const url = env.VITE_SUPABASE_URL?.replace(/\/$/u, '');
    const key = env.VITE_SUPABASE_PUBLISHABLE_KEY;
    const token = readAccessToken();

    if (url === undefined || key === undefined || token === null) return null;

    const response = await fetch(url + '/rest/v1/rpc/' + name, {
      method: 'POST',
      headers: {
        apikey: key,
        Authorization: 'Bearer ' + token,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
      ...(signal === undefined ? {} : { signal }),
    });

    if (!response.ok) return null;
    return (await response.json()) as T;
  }

  async loadOverview(signal?: AbortSignal): Promise<AdminOverviewResult> {
    const rows = await this.rpc<OverviewRow[]>('admin_overview_v1', {}, signal);
    const row = rows?.[0];
    if (row === undefined) return { ok: false, message: 'Administración no disponible.' };

    const overview: AdminOverview = {
      profiles: Number(row.profiles),
      pendingInvitations: Number(row.pending_invitations),
      redeemedInvitations: Number(row.redeemed_invitations),
      superusers: Number(row.superusers),
      auditEvents: Number(row.audit_events),
    };
    return { ok: true, overview };
  }

  async loadAgencies(signal?: AbortSignal): Promise<AdminAgenciesResult> {
    const rows = await this.rpc<AgencyRow[]>('admin_agencies_v1', {}, signal);
    if (rows === null) return { ok: false, message: 'No fue posible leer agencias.' };
    const agencies: AdminAgency[] = rows.map((row) => ({
      agencyId: row.agency_id,
      name: row.name,
      status: row.status,
    }));
    return { ok: true, agencies };
  }

  async loadInvitations(signal?: AbortSignal): Promise<AdminInvitationsResult> {
    const rows = await this.rpc<InvitationRow[]>('admin_invitations_v1', { p_limit: 100 }, signal);
    if (rows === null) return { ok: false, message: 'No fue posible leer invitaciones.' };
    const invitations: AdminInvitation[] = rows.map((row) => ({
      invitationId: row.invitation_id,
      email: row.email,
      invitedRole: row.invited_role,
      status: row.status,
      agencyId: row.agency_id,
      agencyName: row.agency_name,
      invitedBy: row.invited_by,
      invitedByName: row.invited_by_name,
      redeemedBy: row.redeemed_by,
      redeemedByName: row.redeemed_by_name,
      expiresAt: row.expires_at,
      createdAt: row.created_at,
    }));
    return { ok: true, invitations };
  }

  async loadAccounts(signal?: AbortSignal): Promise<AdminAccountsResult> {
    const rows = await this.rpc<AccountRow[]>('admin_accounts_v1', { p_limit: 100 }, signal);
    if (rows === null) return { ok: false, message: 'No fue posible leer cuentas.' };
    const accounts: AdminAccount[] = rows.map((row) => ({
      profileId: row.profile_id,
      displayName: row.display_name,
      email: row.email,
      profileStatus: row.profile_status,
      publicName: row.public_name,
      agencyName: row.agency_name,
      agencyRole: row.agency_role,
      globalRoles: row.global_roles,
      createdAt: row.created_at,
    }));
    return { ok: true, accounts };
  }

  async loadAudit(signal?: AbortSignal): Promise<AdminAuditResult> {
    const rows = await this.rpc<AuditRow[]>('admin_audit_v1', { p_limit: 100 }, signal);
    if (rows === null) return { ok: false, message: 'No fue posible leer auditoría.' };
    const events: AdminAuditEvent[] = rows.map((row) => ({
      eventId: Number(row.event_id),
      actorProfileId: row.actor_profile_id,
      actorName: row.actor_name,
      action: row.action,
      entityType: row.entity_type,
      entityId: row.entity_id,
      metadata: row.metadata,
      occurredAt: row.occurred_at,
    }));
    return { ok: true, events };
  }

  async loadPlans(signal?: AbortSignal): Promise<AdminPlansResult> {
    const rows = await this.rpc<PlanRow[]>('commercial_plans_v1', {}, signal);
    if (rows === null) return { ok: false, message: 'No fue posible leer planes comerciales.' };
    const plans: CommercialPlan[] = rows.map((row) => ({
      planKey: row.plan_key,
      displayName: row.display_name,
      dependentLimit: Number(row.dependent_limit),
      propertyLimit: Number(row.property_limit),
      photosPerProperty: Number(row.photos_per_property),
      paymentMode: row.payment_mode,
    }));
    return { ok: true, plans };
  }

  async loadEntitlements(signal?: AbortSignal): Promise<AdminEntitlementsResult> {
    const rows = await this.rpc<EntitlementRow[]>('admin_entitlements_v1', {}, signal);
    if (rows === null) return { ok: false, message: 'No fue posible leer entitlements.' };
    const entitlements: AdminEntitlement[] = rows.map((row) => ({
      holderProfileId: row.holder_profile_id,
      holderName: row.holder_name,
      planKey: row.plan_key,
      planName: row.plan_name,
      entitlementStatus: row.entitlement_status,
      dependentLimit: Number(row.dependent_limit),
      dependentsUsed: Number(row.dependents_used),
      propertyLimit: Number(row.property_limit),
      propertiesUsed: Number(row.properties_used),
      photosPerProperty: Number(row.photos_per_property),
      paymentMode: row.payment_mode,
    }));
    return { ok: true, entitlements };
  }

  async loadDependents(
    holderProfileId: string,
    signal?: AbortSignal,
  ): Promise<AdminDependentsResult> {
    const rows = await this.rpc<DependentRow[]>(
      'admin_dependents_v1',
      { p_holder_profile_id: holderProfileId },
      signal,
    );
    if (rows === null) {
      return { ok: false, message: 'No fue posible leer dependientes.' };
    }
    const dependents: AdminDependent[] = rows.map((row) => ({
      dependentProfileId: row.dependent_profile_id,
      displayName: row.display_name,
      status: row.status,
    }));
    return { ok: true, dependents };
  }

  async createInvitation(
    email: string,
    role: string,
    agencyId: string | null,
    expiresDays: number,
    signal?: AbortSignal,
  ): Promise<AdminCreateInvitationResult> {
    const secret = await invitationSecret();
    const invitationId = await this.rpc<string>(
      'admin_create_invitation_v1',
      {
        p_email: email,
        p_role: role,
        p_agency_id: agencyId,
        p_token_digest: secret.digest,
        p_expires_days: expiresDays,
      },
      signal,
    );
    if (invitationId === null) return { ok: false, message: 'No fue posible crear la invitación.' };
    return {
      ok: true,
      invitation: { invitationId, email: email.trim().toLowerCase(), token: secret.token },
    };
  }

  async revokeInvitation(invitationId: string, signal?: AbortSignal): Promise<AdminMutationResult> {
    const result = await this.rpc<boolean>('admin_revoke_invitation_v1', { p_invitation_id: invitationId }, signal);
    return result === true ? { ok: true } : { ok: false, message: 'No fue posible revocar la invitación.' };
  }

  async setProfileStatus(
    profileId: string,
    status: 'active' | 'suspended' | 'disabled',
    signal?: AbortSignal,
  ): Promise<AdminMutationResult> {
    const result = await this.rpc<string>('admin_set_profile_status_v1', { p_profile_id: profileId, p_status: status }, signal);
    return result === null ? { ok: false, message: 'No fue posible actualizar la cuenta.' } : { ok: true };
  }

  async setGlobalAdmin(profileId: string, enabled: boolean, signal?: AbortSignal): Promise<AdminMutationResult> {
    const result = await this.rpc<boolean>('admin_set_global_admin_v1', { p_profile_id: profileId, p_enabled: enabled }, signal);
    return result === null ? { ok: false, message: 'No fue posible actualizar el superusuario.' } : { ok: true };
  }

  async setEntitlement(
    profileId: string,
    planKey: string,
    status: 'active' | 'suspended' | 'expired',
    signal?: AbortSignal,
  ): Promise<AdminMutationResult> {
    const result = await this.rpc<boolean>(
      'admin_set_entitlement_v1',
      {
        p_holder_profile_id: profileId,
        p_plan_key: planKey,
        p_status: status,
      },
      signal,
    );
    return result === true
      ? { ok: true }
      : { ok: false, message: 'No fue posible actualizar el plan.' };
  }

  async setDependent(
    holderProfileId: string,
    dependentProfileId: string,
    enabled: boolean,
    signal?: AbortSignal,
  ): Promise<AdminMutationResult> {
    const result = await this.rpc<boolean>(
      'admin_set_dependent_v1',
      {
        p_holder_profile_id: holderProfileId,
        p_dependent_profile_id: dependentProfileId,
        p_enabled: enabled,
      },
      signal,
    );
    return result === null
      ? { ok: false, message: 'No fue posible actualizar el dependiente.' }
      : { ok: true };
  }
  async createAgency(
    name: string,
    slug: string,
    city: string,
    state: string,
    phone: string,
    website: string,
    signal?: AbortSignal,
  ): Promise<AdminMutationResult> {
    const result = await this.rpc<string>(
      'admin_create_agency_v1',
      {
        p_name: name,
        p_slug: slug,
        p_city: city,
        p_state: state,
        p_phone: phone.trim() === '' ? null : phone.trim(),
        p_website: website.trim() === '' ? null : website.trim(),
      },
      signal,
    );
    return result === null
      ? { ok: false, message: 'No fue posible crear la agencia.' }
      : { ok: true };
  }

  async linkProfessional(
    profileId: string,
    agencyId: string,
    publicName: string,
    membershipRole: string,
    professionalRole: string,
    signal?: AbortSignal,
  ): Promise<AdminMutationResult> {
    const result = await this.rpc<string>(
      'admin_link_professional_v1',
      {
        p_profile_id: profileId,
        p_agency_id: agencyId,
        p_public_name: publicName,
        p_membership_role: membershipRole,
        p_professional_role: professionalRole,
        p_email: null,
        p_phone: null,
      },
      signal,
    );
    return result === null
      ? { ok: false, message: 'No fue posible vincular el perfil profesional.' }
      : { ok: true };
  }

}
